import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Drawer, Badge, Avatar, Divider } from 'antd';
import {
	DashboardOutlined, CarOutlined, CopyOutlined, AppstoreOutlined,
	DiffOutlined, UserOutlined, SettingOutlined, InfoCircleOutlined,
	LineChartOutlined, LogoutOutlined, BellOutlined, NotificationOutlined,
	TeamOutlined, CloseOutlined, FileTextOutlined, RightOutlined,
	SafetyCertificateOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { setConfig } from '../store/actions';
import Config from '../Config';
import { doLogout } from '../utils/sessionWS';

const config = new Config();

// ── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
	{ key: 'dashboard',    icon: DashboardOutlined, label: 'Dashboard'   },
	{ key: 'pendaftaran',  icon: FileTextOutlined,  label: 'Daftar'      },
	{ key: 'transaksi',    icon: CarOutlined,        label: 'Transaksi'   },
	{ key: 'laporan',      icon: CopyOutlined,       label: 'Laporan'     },
	{ key: 'lainnya',      icon: AppstoreOutlined,   label: 'Lainnya'     },
];

// Paths that belong to "Pendaftaran" tab (order matters — more specific first)
const PENDAFTARAN_PATHS = [
	'/admin/transaksi/pendaftaran-eri',
	'/admin/transaksi/penetapan-biaya-eri',
	'/admin/transaksi/pendaftaran',
	'/admin/transaksi/penetapan-biaya',
];

function getActiveTab(pathname) {
	if (pathname === '/admin/index' || pathname === '/admin' || pathname === '/admin/')
		return 'dashboard';
	if (PENDAFTARAN_PATHS.some(p => pathname === p || pathname.startsWith(p + '/')))
		return 'pendaftaran';
	if (pathname.startsWith('/admin/transaksi'))
		return 'transaksi';
	if (pathname.startsWith('/admin/laporan'))
		return 'laporan';
	return 'lainnya';
}

// ── Sheet item component ─────────────────────────────────────────────────────
function SheetItem({ icon: Icon, label, path, onClick }) {
	const inner = (
		<div style={{
			display: 'flex', alignItems: 'center', gap: 14,
			padding: '13px 20px', cursor: 'pointer',
			borderBottom: '1px solid #f3f4f6',
			transition: 'background 0.15s',
		}}
			onMouseDown={e => e.currentTarget.style.background = '#f0f4ff'}
			onMouseUp={e => e.currentTarget.style.background = ''}
			onTouchStart={e => e.currentTarget.style.background = '#f0f4ff'}
			onTouchEnd={e => e.currentTarget.style.background = ''}
		>
			<div style={{
				width: 36, height: 36, borderRadius: 10, flexShrink: 0,
				background: 'linear-gradient(135deg, #0f284715, #1e4d8a20)',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
			}}>
				<Icon style={{ fontSize: 16, color: '#1e4d8a' }} />
			</div>
			<span style={{ flex: 1, fontSize: 14, color: '#1f2937', fontWeight: 500 }}>{label}</span>
			<RightOutlined style={{ fontSize: 11, color: '#9ca3af' }} />
		</div>
	);
	if (path) {
		return <Link to={path} onClick={onClick} style={{ display: 'block', color: 'inherit' }}>{inner}</Link>;
	}
	return <div onClick={onClick}>{inner}</div>;
}

// ── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ label }) {
	return (
		<div style={{
			padding: '10px 20px 6px',
			fontSize: 11, fontWeight: 700, color: '#6b7280',
			letterSpacing: '0.06em', textTransform: 'uppercase',
			background: '#f9fafb', borderBottom: '1px solid #f0f0f0',
		}}>
			{label}
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────
class MobileBottomNav extends React.Component {
	constructor(props) {
		super(props);
		this.state = { activeSheet: null };
	}

	handleLogout = () => {
		// Logout terpusat — pakai socket device milik AdminLayout (registry),
		// tidak lagi membuka WebSocket baru di sini.
		doLogout(this.props.setConfig);
	}

	openSheet = (sheet) => this.setState({ activeSheet: sheet })
	closeSheet = () => this.setState({ activeSheet: null })

	handleTabClick = (tab) => {
		if (tab === 'dashboard') {
			this.closeSheet();
			this.props.history.push('/admin/index');
		} else {
			this.setState(prev => ({
				activeSheet: prev.activeSheet === tab ? null : tab,
			}));
		}
	}

	// ── Sheet content builders ────────────────────────────────────────────────
	buildPendaftaranItems() {
		const { akses, waktu_akses } = this.props;
		const bypass = waktu_akses && waktu_akses.is_bypass;
		return [
			{
				key: 'pdftrn_eri', icon: FileTextOutlined, label: 'Pendaftaran dari ERI',
				path: '/admin/transaksi/pendaftaran-eri',
				show: akses == null || (!bypass && akses.transaksi_pendaftaran_eri),
			},
			{
				key: 'penetapan_eri', icon: FileTextOutlined, label: 'Penetapan ERI',
				path: '/admin/transaksi/penetapan-biaya-eri',
				show: akses == null || (!bypass && akses.transaksi_penetapan_eri),
			},
			{
				key: 'pdftrn', icon: FileTextOutlined, label: 'Pendaftaran Lama',
				path: '/admin/transaksi/pendaftaran',
				show: akses == null || akses.transaksi_pendaftaran,
			},
			{
				key: 'penetapan', icon: FileTextOutlined, label: 'Penetapan',
				path: '/admin/transaksi/penetapan-biaya',
				show: akses == null || akses.transaksi_penetapan,
			},
		].filter(i => i.show);
	}

	buildTransaksiItems() {
		const { akses, waktu_akses } = this.props;
		const bypass = waktu_akses && waktu_akses.is_bypass;
		return [
			{ key: 'blokir',     icon: CarOutlined, label: 'Blokir',   path: '/admin/transaksi/blokir',   show: akses == null || akses.blokir },
			{ key: 'bayar',      icon: CarOutlined, label: 'Pembayaran', path: '/admin/transaksi/pembayaran', show: akses == null || bypass || akses.transaksi_pembayaran },
			{ key: 'skf',        icon: CarOutlined, label: 'Surat Keterangan Fiskal', path: '/admin/transaksi/surat-keterangan-fiskal', show: akses == null || bypass || akses.transaksi_surat_keterangan_fiskal },
			{ key: 'batal_nf',   icon: CarOutlined, label: 'Batal Notice', path: '/admin/transaksi/cancel-notice', show: akses == null || akses.transaksi_cancle_notice },
			{ key: 'hist_nf',    icon: CarOutlined, label: 'Histori Notice', path: '/admin/transaksi/histori-notice', show: akses == null || akses.transaksi_histori_notice },
			{ key: 'kor_nf',     icon: CarOutlined, label: 'Koreksi Nomor Notice', path: '/admin/transaksi/koreksi-nomor-notice', show: akses == null || akses.transaksi_koreksi_nomor_notice },
			{ key: 'sinkron_nf', icon: CarOutlined, label: 'Sinkronisasi Notice', path: '/admin/transaksi/sinkron-notice', show: akses == null || akses.transaksi_sinkron_notice },
			{ key: 'audit_rusak', icon: CarOutlined, label: 'Audit Notice Rusak', path: '/admin/transaksi/audit-notice-rusak', show: akses == null || akses.transaksi_audit_notice_rusak },
			{ key: 'iwkbu_t',    icon: CarOutlined, label: 'IWKBU', path: '/admin/transaksi/iwkbu', show: akses == null || akses.iwkbu },
		].filter(i => i.show);
	}

	buildLaporanItems() {
		const { akses } = this.props;
		return [
			{ key: 'pnbp',      label: 'PNBP',                        path: '/admin/laporan/pnbp',                    show: akses == null || akses.laporan_pnbp },
			{ key: 'kasir',     label: 'Kasir',                        path: '/admin/laporan/kasir',                   show: akses == null || akses.laporan_kasir },
			{ key: 'teller',    label: 'Penerimaan Teller',            path: '/admin/laporan/teller',                  show: akses == null || akses.laporan_teller },
			{ key: 'rekning',   label: 'Perekening',                   path: '/admin/laporan/perekening',              show: akses == null || akses.laporan_perekening },
			{ key: 'pemutihan', label: 'Pemutihan',                    path: '/admin/laporan/pemutihan',               show: akses == null || akses.laporan_pemutihan },
			{ key: 'cstnk',     label: 'Cetak STNK',                  path: '/admin/laporan/cetak-stnk',              show: akses == null || akses.laporan_cetak_stnk },
			{ key: 'bp',        label: 'Bendahara Penerimaan',         path: '/admin/laporan/bendahara-penerimaan',    show: akses == null || akses.laporan_bendahara_penerimaan },
			{ key: 'bpkb',      label: 'Bendahara PKB & BBNKB',       path: '/admin/laporan/bendahara-pkb',           show: akses == null || akses.laporan_bendaraha_pkb_bbnkb },
			{ key: 'hpkb',      label: 'Penetapan PKB & BBNKB',       path: '/admin/laporan/harian-pkb',              show: akses == null || akses.laporan_penetapan_pkb_bbnkb },
			{ key: 'pkbwarna',  label: 'Penerimaan PKB Warna Plat',   path: '/admin/laporan/penerimaan-pkb-warna',    show: akses == null || akses.laporan_penerimaan_pkb_plat },
			{ key: 'sum32',     label: 'SUM 32',                       path: '/admin/laporan/sum-32',                  show: akses == null || akses.laporan_sum32 },
			{ key: 'sts',       label: 'STS Harian',                   path: '/admin/laporan/sts-harian',              show: akses == null || akses.laporan_sts },
			{ key: 'swdkllj',   label: 'SWDKLLJ',                      path: '/admin/laporan/swdkllj',                 show: akses == null || akses.laporan_swdkllj },
			{ key: 'penyet',    label: 'Pengantar Penyetoran Uang',    path: '/admin/laporan/penyetoran-uang',         show: akses == null || akses.penyetoran_uang },
			{ key: 'bbnkb',     label: 'Penerimaan BBNKB & PKB',      path: '/admin/laporan/bbnkb-pkb',              show: akses == null || akses.laporan_penerimaan_bbnkb_pkb },
			{ key: 'notice',    label: 'Notice',                       path: '/admin/laporan/notice',                  show: akses == null || akses.laporan_notice },
			{ key: 'nrusak',    label: 'Notice Rusak',                 path: '/admin/laporan/notice-rusak',            show: akses == null || akses.laporan_notice_rusak },
			{ key: 'pnrusak',   label: 'Penggunaan Notice & Notice Rusak', path: '/admin/laporan/penggunaan-notice-rusak', show: akses == null || akses.laporan_penggunaan_notice_rusak },
			{ key: 'overlapnotice', label: 'Overlap Kode Notice',      path: '/admin/laporan/overlap-notice',          show: akses == null || akses.laporan_overlap_notice },
			{ key: 'babatal',   label: 'BA Batal SKPD',               path: '/admin/laporan/ba-batal-skpd',           show: akses == null || akses.laporan_ba_batal_skpd },
			{ key: 'statusprinter', label: 'Status Printer',          path: '/admin/laporan/status-printer',          show: akses == null || akses.laporan_status_printer },
			{ key: 'auditnik',  label: 'Audit NIK',                    path: '/admin/laporan/audit-nik',               show: akses == null || akses.laporan_audit_nik },
			{ key: 'dupnopol',  label: 'Duplikat Nopol Aktif',          path: '/admin/laporan/duplikat-nopol-aktif',    show: akses == null || akses.laporan_duplikat_nopol_aktif },
		].filter(i => i.show);
	}

	// ── Sheet renderers ───────────────────────────────────────────────────────
	renderSheetHeader(icon, title) {
		return (
			<div style={{
				background: 'linear-gradient(135deg, #0f2847, #1e4d8a)',
				padding: '14px 20px',
				display: 'flex', alignItems: 'center', justifyContent: 'space-between',
			}}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<div style={{
						width: 32, height: 32, borderRadius: 8,
						background: 'rgba(255,255,255,0.15)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
					}}>
						{React.createElement(icon, { style: { color: '#fff', fontSize: 15 } })}
					</div>
					<span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{title}</span>
				</div>
				<CloseOutlined
					onClick={this.closeSheet}
					style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, cursor: 'pointer', padding: 4 }}
				/>
			</div>
		);
	}

	renderPendaftaranSheet() {
		const items = this.buildPendaftaranItems();
		const h = Math.min(window.innerHeight * 0.75, items.length * 62 + 60);
		return (
			<Drawer placement="bottom" closable={false} onClose={this.closeSheet}
				visible={this.state.activeSheet === 'pendaftaran'}
				height={h} bodyStyle={{ padding: 0 }}
			>
				{this.renderSheetHeader(FileTextOutlined, 'Pendaftaran')}
				<div style={{ overflowY: 'auto', maxHeight: h - 60 }}>
					{items.length === 0
						? <p style={{ padding: 20, color: '#888', textAlign: 'center' }}>Tidak ada akses menu</p>
						: items.map(item => (
							<SheetItem key={item.key} icon={item.icon} label={item.label} path={item.path} onClick={this.closeSheet} />
						))
					}
				</div>
			</Drawer>
		);
	}

	renderTransaksiSheet() {
		const items = this.buildTransaksiItems();
		const maxH = Math.min(window.innerHeight * 0.78, 520);
		return (
			<Drawer placement="bottom" closable={false} onClose={this.closeSheet}
				visible={this.state.activeSheet === 'transaksi'}
				height={maxH} bodyStyle={{ padding: 0 }}
			>
				{this.renderSheetHeader(CarOutlined, 'Transaksi')}
				<div style={{ overflowY: 'auto', height: maxH - 60 }}>
					{items.map(item => (
						<SheetItem key={item.key} icon={item.icon} label={item.label} path={item.path} onClick={this.closeSheet} />
					))}
				</div>
			</Drawer>
		);
	}

	renderLaporanSheet() {
		const items = this.buildLaporanItems();
		const maxH = Math.min(window.innerHeight * 0.82, 560);
		return (
			<Drawer placement="bottom" closable={false} onClose={this.closeSheet}
				visible={this.state.activeSheet === 'laporan'}
				height={maxH} bodyStyle={{ padding: 0 }}
			>
				{this.renderSheetHeader(CopyOutlined, 'Laporan')}
				<div style={{ overflowY: 'auto', height: maxH - 60 }}>
					{items.map(item => (
						<SheetItem key={item.key} icon={CopyOutlined} label={item.label} path={item.path} onClick={this.closeSheet} />
					))}
				</div>
			</Drawer>
		);
	}

	renderLainnyaSheet() {
		const { akses, waktu_akses, level_user, nama, pp, username,
			cancelNoticePendingCount, pengumumanBadgeCount, activeUsersCount } = this.props;
		const bypass = waktu_akses && waktu_akses.is_bypass;
		const maxH = Math.min(window.innerHeight * 0.9, 620);

		const showMaster      = akses == null || akses.master;
		const showJasaRaharja = akses == null || akses.jasa_raharja;
		const showPegawai     = akses == null || akses.pegawai;
		const showPengaturan  = akses == null || akses.pengaturan;
		const showInformasi   = akses == null || akses.informasi;
		const showAnalitik    = akses == null || akses.analitik;
		const showSecrets     = akses == null || akses.secrets;
		const showBatalNotice = akses && akses.transaksi_cancle_notice_approve;
		const showUserAktif   = level_user != null && level_user <= 2;

		return (
			<Drawer placement="bottom" closable={false} onClose={this.closeSheet}
				visible={this.state.activeSheet === 'lainnya'}
				height={maxH} bodyStyle={{ padding: 0 }}
			>
				{this.renderSheetHeader(AppstoreOutlined, 'Lainnya')}
				<div style={{ overflowY: 'auto', height: maxH - 60 }}>

					{/* ── Notifikasi ── */}
					<SectionHeader label="Notifikasi" />
					<div style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
						{showBatalNotice && (
							<button onClick={() => { this.props.setConfig({ openCancelNoticeNotif: true }); this.closeSheet(); }}
								style={quickBtnStyle('#ff4d4f22', '#ff4d4f')}>
								<Badge count={cancelNoticePendingCount || 0} size="small" offset={[4, -2]}>
									<BellOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />
								</Badge>
								<span style={{ fontSize: 11, color: '#ff4d4f', fontWeight: 600, marginTop: 4 }}>Batal Notice</span>
							</button>
						)}
						<button onClick={() => { this.props.setConfig({ openPengumumanPanel: true }); this.closeSheet(); }}
							style={quickBtnStyle('#818cf822', '#818cf8')}>
							<Badge count={pengumumanBadgeCount || 0} size="small" offset={[4, -2]}>
								<NotificationOutlined style={{ fontSize: 18, color: '#818cf8' }} />
							</Badge>
							<span style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, marginTop: 4 }}>Pengumuman</span>
						</button>
						{showUserAktif && (
							<button onClick={() => { this.props.setConfig({ openUserAktifPanel: true }); this.closeSheet(); }}
								style={quickBtnStyle('#34d39922', '#34d399')}>
								<Badge count={activeUsersCount || 0} size="small" offset={[4, -2]}
									style={{ backgroundColor: '#34d399', fontSize: 10 }}>
									<TeamOutlined style={{ fontSize: 18, color: '#34d399' }} />
								</Badge>
								<span style={{ fontSize: 11, color: '#34d399', fontWeight: 600, marginTop: 4 }}>User Aktif</span>
							</button>
						)}
					</div>

					{/* Log Aktivitas Saya DIPINDAH ke ikon topbar (AdminHeader.jsx,
					    sejajar Pengumuman/Panduan) - tidak lagi di sini, lihat
					    diskusi rencana 2026-10-17. "Log Aktivitas User" tetap di
					    sini (khusus admin/superadmin, level_user<=2 sama persis
					    pengecekan backend IsAdminOrSuperadmin). */}
					{level_user != null && level_user <= 2 && <>
						<SectionHeader label="Log Aktivitas" />
						<SheetItem icon={HistoryOutlined} label="Log Aktivitas User" path="/admin/activitylog/kelola" onClick={this.closeSheet} />
					</>}

					{/* ── Master ── */}
					{showMaster && <>
						<SectionHeader label="Master Data" />
						{akses == null || akses.master_negara       ? <SheetItem icon={DiffOutlined} label="Negara"             path="/admin/negara"                   onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_merek        ? <SheetItem icon={DiffOutlined} label="Merek"              path="/admin/merek"                    onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_jenis_kendaraan ? <SheetItem icon={DiffOutlined} label="Jenis Kendaraan" path="/admin/jenis-kendaraan"          onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_Tipe         ? <SheetItem icon={DiffOutlined} label="Tipe"               path="/admin/tipe"                     onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_njkb     ? <SheetItem icon={DiffOutlined} label="NJKB"               path="/admin/njkb"                     onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_layanan      ? <SheetItem icon={DiffOutlined} label="Layanan"            path="/admin/layanan"                  onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_gudang_notice ? <SheetItem icon={DiffOutlined} label="Gudang Notice"     path="/admin/gudang-notice"            onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_mac_address   ? <SheetItem icon={DiffOutlined} label="Perangkat / MAC Address" path="/admin/mac-address"       onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_warna        ? <SheetItem icon={DiffOutlined} label="Warna"              path="/admin/warna"                    onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_warna_plat   ? <SheetItem icon={DiffOutlined} label="Warna Plat"         path="/admin/warna-plat"               onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_bahan_bakar  ? <SheetItem icon={DiffOutlined} label="Bahan Bakar"        path="/admin/bahan-bakar"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_fungsi       ? <SheetItem icon={DiffOutlined} label="Fungsi"             path="/admin/fungsi"                   onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_payment      ? <SheetItem icon={DiffOutlined} label="Payment"            path="/admin/payment"                  onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_kendaraan    ? <SheetItem icon={DiffOutlined} label="Kendaraan"          path="/admin/kendaraan"                onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_tanda_tangan ? <SheetItem icon={DiffOutlined} label="Tanda Tangan SKPD"  path="/admin/tanda_tangan"            onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_ttd_laporan  ? <SheetItem icon={DiffOutlined} label="Tanda Tangan Laporan" path="/admin/tanda_tangan_laporan" onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_kepemilikan  ? <SheetItem icon={DiffOutlined} label="Kepemilikan"        path="/admin/kepemilikan"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_pekerjaan    ? <SheetItem icon={DiffOutlined} label="Pekerjaan"          path="/admin/pekerjaan"                onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_dealer       ? <SheetItem icon={DiffOutlined} label="Dealer"             path="/admin/dealer"                   onClick={this.closeSheet} /> : null}
						{level_user === 1                            ? <SheetItem icon={DiffOutlined} label="Kabupaten"          path="/admin/kabupaten"                onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_njkb     ? <SheetItem icon={DiffOutlined} label="Import NJKB"        path="/admin/njkb-import"              onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Jasa Raharja ── */}
					{showJasaRaharja && <>
						<SectionHeader label="Jasa Raharja" />
						{akses == null || akses.jasa_raharja_resend     ? <SheetItem icon={SafetyCertificateOutlined} label="Resend Jasa Raharja"       path="/admin/jasa-raharja/resend"          onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_iwkbu            ? <SheetItem icon={SafetyCertificateOutlined} label="Data IWKBU"                path="/admin/iwkbu"                        onClick={this.closeSheet} /> : null}
						{akses == null || akses.master_golongan         ? <SheetItem icon={SafetyCertificateOutlined} label="Golongan"                  path="/admin/golongan"                     onClick={this.closeSheet} /> : null}
						{akses == null || akses.transaksi_jasa_raharja  ? <SheetItem icon={SafetyCertificateOutlined} label="Transaksi JR"              path="/admin/transaksi/jasaraharja"        onClick={this.closeSheet} /> : null}
						{akses == null || akses.transaksi_validasi_setoran ? <SheetItem icon={SafetyCertificateOutlined} label="Validasi Setoran SWDKLLJ" path="/admin/transaksi/validasi-setoran" onClick={this.closeSheet} /> : null}
						{akses == null || akses.transaksi_smdd          ? <SheetItem icon={SafetyCertificateOutlined} label="SMDD"                      path="/admin/transaksi/smdd"               onClick={this.closeSheet} /> : null}
						{akses == null || akses.cross_check             ? <SheetItem icon={SafetyCertificateOutlined} label="Cross Check SWDKLLJ"       path="/admin/transaksi/uji-silang"         onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Pegawai ── */}
					{showPegawai && <>
						<SectionHeader label="Pegawai" />
						{akses == null || akses.pegawai_jabatan    ? <SheetItem icon={UserOutlined} label="Jabatan"            path="/admin/jabatan"            onClick={this.closeSheet} /> : null}
						{akses == null || akses.waktu_operasional  ? <SheetItem icon={UserOutlined} label="Waktu Operasional"  path="/admin/waktu-operasional"  onClick={this.closeSheet} /> : null}
						{akses == null || akses.pegawai_upt        ? <SheetItem icon={UserOutlined} label="UPT"                path="/admin/upt"                onClick={this.closeSheet} /> : null}
						{akses == null || akses.pegawai_bidang     ? <SheetItem icon={UserOutlined} label="Bidang"             path="/admin/bidang"             onClick={this.closeSheet} /> : null}
						{akses == null || akses.pegawai_pegawai    ? <SheetItem icon={UserOutlined} label="Pegawai"            path="/admin/pegawai"            onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Pengaturan ── */}
					{showPengaturan && <>
						<SectionHeader label="Pengaturan" />
						{akses == null || akses.pengaturan_dikon               ? <SheetItem icon={SettingOutlined} label="Diskon"                path="/admin/pengaturan/diskon"               onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_reward_diskon        ? <SheetItem icon={SettingOutlined} label="Reward Tepat Waktu"   path="/admin/pengaturan/reward-tepat-waktu"   onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_hari_libur           ? <SheetItem icon={SettingOutlined} label="Hari Libur"            path="/admin/pengaturan/hari-libur"           onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_umum                 ? <SheetItem icon={SettingOutlined} label="Umum"                  path="/admin/pengaturan/umum"                 onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_swdkllj              ? <SheetItem icon={SettingOutlined} label="SWDKLLJ"               path="/admin/pengaturan/swdkllj"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_adm_stnk             ? <SheetItem icon={SettingOutlined} label="Adm. STNK"             path="/admin/pengaturan/admstnk"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_adm_tnkb             ? <SheetItem icon={SettingOutlined} label="Adm. TNKB"             path="/admin/pengaturan/admtnkb"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_denda_pkb            ? <SheetItem icon={SettingOutlined} label="Denda PKB"              path="/admin/pengaturan/denda-pkb"            onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_denda_swdkllj        ? <SheetItem icon={SettingOutlined} label="Denda SWDKLLJ"          path="/admin/pengaturan/denda-swdkllj"        onClick={this.closeSheet} /> : null}
						{akses == null || bypass || akses.pengaturan_notice     ? <SheetItem icon={SettingOutlined} label="Notice"                 path="/admin/pengaturan/notice"               onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_printer              ? <SheetItem icon={SettingOutlined} label="Printer"                path="/admin/pengaturan/printer"              onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_target_pendapatan    ? <SheetItem icon={SettingOutlined} label="Target Pendapatan"      path="/admin/pengaturan/target-pendapatan"    onClick={this.closeSheet} /> : null}
						{akses == null || akses.pengaturan_migrasi_legacy_notice ? <SheetItem icon={SettingOutlined} label="Migrasi Legacy Notice" path="/admin/pengaturan/migrasi-legacy-notice" onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Informasi ── */}
					{showInformasi && <>
						<SectionHeader label="Informasi" />
						{akses == null || akses.informasi_informasi_kendaraan ? <SheetItem icon={InfoCircleOutlined} label="Informasi Kendaraan" path="/admin/informasi/informasi-kendaraan" onClick={this.closeSheet} /> : null}
						{akses == null || akses.informasi_cari_kendaraan     ? <SheetItem icon={InfoCircleOutlined} label="Cari Kendaraan"      path="/admin/informasi/cari-kendaraan"      onClick={this.closeSheet} /> : null}
						{akses == null || akses.informasi_migrasi             ? <SheetItem icon={InfoCircleOutlined} label="Migrasi"             path="/admin/informasi/migrasi"             onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Analitik ── */}
					{showAnalitik && <>
						<SectionHeader label="Analitik" />
						{akses == null || akses.analitik_proyeksi_potensi_pkb ? <SheetItem icon={LineChartOutlined} label="Proyeksi Potensi PKB" path="/admin/analitik/proyeksi-potensi-pkb" onClick={this.closeSheet} /> : null}
						{akses == null || akses.analitik_tunggakan            ? <SheetItem icon={LineChartOutlined} label="Tunggakan Kendaraan"  path="/admin/analitik/tunggakan"            onClick={this.closeSheet} /> : null}
						{akses == null || akses.analitik_list_tunggakan       ? <SheetItem icon={LineChartOutlined} label="List Tunggakan"       path="/admin/analitik/list-tunggakan"       onClick={this.closeSheet} /> : null}
					</>}

					{/* ── Pengaturan Secrets ── */}
					{showSecrets && <>
						<SectionHeader label="Pengaturan Secrets" />
						{akses == null || akses.secrets_apikey               ? <SheetItem icon={SettingOutlined} label="API Key"                path="/admin/secrets/apikey"                onClick={this.closeSheet} /> : null}
						{akses == null || akses.secrets_print_client_config  ? <SheetItem icon={SettingOutlined} label="Konfigurasi App Cetak"   path="/admin/secrets/konfigurasi-app-cetak" onClick={this.closeSheet} /> : null}
					</>}

					{/* ── User ── */}
					<Divider style={{ margin: '8px 0 0' }} />
					<div style={{
						display: 'flex', alignItems: 'center', gap: 14,
						padding: '14px 20px 20px',
						background: '#f9fafb',
					}}>
						<Avatar
							size={40} src={pp && !pp.endsWith('user.png') ? pp : undefined}
							style={{ background: '#1e4d8a', flexShrink: 0 }}
						>
							{(!pp || pp.endsWith('user.png'))
								? (nama ? nama[0].toUpperCase() : <UserOutlined />)
								: null}
						</Avatar>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{nama || username}
							</div>
							<Link to="/admin/profil" onClick={this.closeSheet}
								style={{ fontSize: 12, color: '#1e4d8a', fontWeight: 500 }}>
								Lihat Profil
							</Link>
						</div>
						<button onClick={this.handleLogout} style={{
							background: '#fee2e2', border: 'none', borderRadius: 8,
							padding: '8px 12px', cursor: 'pointer',
							display: 'flex', alignItems: 'center', gap: 6,
							color: '#dc2626', fontWeight: 600, fontSize: 13,
						}}>
							<LogoutOutlined style={{ fontSize: 14 }} />
							Keluar
						</button>
					</div>
				</div>
			</Drawer>
		);
	}

	// ── Tab bar ───────────────────────────────────────────────────────────────
	render() {
		const { location, cancelNoticePendingCount, pengumumanBadgeCount } = this.props;
		const { activeSheet } = this.state;
		const pathname = location ? location.pathname : '';
		const activeTab = activeSheet || getActiveTab(pathname);
		const lainnyaBadge = (cancelNoticePendingCount || 0) + (pengumumanBadgeCount || 0);

		return (
			<>
				{/* Bottom tab bar */}
				<div style={{
					position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
					height: 64,
					background: '#fff',
					boxShadow: '0 -2px 12px rgba(0,0,0,0.10)',
					display: 'flex', alignItems: 'stretch',
					borderTop: '1px solid #eef1f6',
				}}>
					{TABS.map(tab => {
						const isActive = activeTab === tab.key;
						const Icon = tab.icon;
						let badge = 0;
						if (tab.key === 'lainnya') badge = lainnyaBadge;

						return (
							<button
								key={tab.key}
								onClick={() => this.handleTabClick(tab.key)}
								style={{
									flex: 1, border: 'none', background: 'transparent',
									display: 'flex', flexDirection: 'column',
									alignItems: 'center', justifyContent: 'center',
									gap: 3, cursor: 'pointer', padding: '6px 4px',
									position: 'relative',
									transition: 'background 0.15s',
								}}
							>
								{isActive && (
									<div style={{
										position: 'absolute', top: 0, left: '20%', right: '20%',
										height: 3, borderRadius: '0 0 3px 3px',
										background: 'linear-gradient(90deg, #0f2847, #1e4d8a)',
									}} />
								)}
								<Badge count={badge} size="small" offset={[4, -2]}>
									<Icon style={{
										fontSize: 20,
										color: isActive ? '#0f2847' : '#9ca3af',
										transition: 'color 0.15s',
									}} />
								</Badge>
								<span style={{
									fontSize: 10, fontWeight: isActive ? 700 : 500,
									color: isActive ? '#0f2847' : '#9ca3af',
									transition: 'color 0.15s', lineHeight: 1.2,
								}}>
									{tab.label}
								</span>
							</button>
						);
					})}
				</div>

				{/* Bottom sheets */}
				{this.renderPendaftaranSheet()}
				{this.renderTransaksiSheet()}
				{this.renderLaporanSheet()}
				{this.renderLainnyaSheet()}
			</>
		);
	}
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function quickBtnStyle(bg, color) {
	return {
		background: bg, border: 'none', borderRadius: 12, padding: '10px 14px',
		cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
		gap: 4, flex: 1,
	};
}

const mapStateToProps = state => state.configReducers;

export default withRouter(connect(mapStateToProps, { setConfig })(MobileBottomNav));
